/* eslint-disable react-hooks/exhaustive-deps */
import { cn } from "../../../../helpers";
import * as React from "react";
import queryString from "query-string";
import { useHistory, useLocation } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import store from "../../../../../store";
import {
    ClientStatus,
    ClinicianCredential,
    DateFormats,
    IAvailabilityWindow,
    IClientBooking,
    IClient,
    IClinician,
    IIntakeAvailability,
    ISessionAttendee,
    ServiceDeliveryOption,
} from "intake_shared/src/types";
import moment from "../../../../../utils/app_moment";
import {
    getAvailableStartDates,
    getClinicianActualAvailableSlots,
    getNowEquivalent,
    getStringFromScheduleDate,
    toTitleCase,
    findLabelByPracticeId,
} from "../../../../../utils/utils";
import { CacheContext } from "../../../../../contexts/CacheContext";
import { getIntakeEmailSignature } from "../../../../../utils/intakeEmailSignature";
import { AclContext } from "../../../../../contexts/AclContext";
import { Dialog } from "../../../bits/Dialog/Dialog";
import { Select } from "../../../bits/Select/Select";
import { FormCheckbox } from "../../../bits/FormCheckbox/FormCheckbox";
import { TextArea } from "../../../bits/TextArea/TextArea";
import { Input } from "../../../bits/Input/Input";
import { Button } from "../../../bits/Button/Button";
import { Badge } from "../../../bits/Badge/Badge";
import Alert from "../../../bits/Alert/Alert";
import { DatePicker } from "../../../bits/DatePicker/DatePicker";
import { InfoIcon } from "../../../icons/Info";
import styles from "./BookClient.module.scss";
import { getV2BadgePropsByPractice } from "../../../../../constants";
import { ClinicianServiceSelection } from "../../../../../store/department";
import Loader from "../../../bits/Loader/Loader";
import {
    formatIntakeAvailabilitySlots,
    getBookingRequestedServiceDefault,
    isEligibleForIntakeSession,
    loadSupervisorsWithIntake,
    resolveBookingRequestedService,
} from "../../../../v2utils";
import {
    reconcileOtherParentConsentAttendee,
    getAppointmentHeldValidationMessage,
} from "../../../../../utils/guardian";
import { isClientMinor } from "../../../../../utils/age";
import {
    buildEmptySessionAttendee,
    getSessionAttendeeMissingFields,
    getUpdatedAttendeeConsentCheck,
    isMinorDob,
    isSessionAttendeeSupportedService,
} from "../../../../../utils/session_attendee_consent";

const MAX_APPT_DATE_DIFF_WEEKS = 4;

interface BookClientProps {
    setClinicianToBook: React.Dispatch<React.SetStateAction<IClinician>>;
    clinicianToBook: IClinician;
    selectedClientToHold: IClient;
    clientConcerns: string;
    setClientConcerns: React.Dispatch<React.SetStateAction<string>>;
    selectedApptToHoldId: string;
    setSelectedApptToHoldId: React.Dispatch<React.SetStateAction<string>>;
    clientCallNotes: string;
    selectedAppointmentFromCard: boolean;
    setSelectedAppointmentFromCard: React.Dispatch<React.SetStateAction<boolean>>;
    clinicianServiceSelections: ClinicianServiceSelection;
}

type Option = {
    value: string;
    label: React.ReactNode;
};

export default function BookClient({
    setClinicianToBook,
    clinicianToBook,
    selectedClientToHold,
    clientConcerns,
    setClientConcerns,
    selectedApptToHoldId,
    setSelectedApptToHoldId,
    clientCallNotes,
    selectedAppointmentFromCard,
    setSelectedAppointmentFromCard,
    clinicianServiceSelections,
}: BookClientProps) {
    const olc = React.useContext(CacheContext);
    const loggedInUserEmail = React.useContext(AclContext)?.authUser.user?.email;
    const requestedServices = olc?.requestedServices || {};
    const allDepartmentsAndServices = olc?.allDepartmentAndServices || {};
    const { addToast } = useToasts();
    const { push } = useHistory();
    const browserLocation = useLocation();
    const parsedQueryParams = queryString.parse(browserLocation.search || "");

    const [selectedApptDate, setSelectedApptDate] = React.useState(moment());
    const [availableApptStartingDates, setAvailableApptStartingDates] =
        React.useState<moment.Moment[]>([]);
    const [inPersonAvailableSlots, setInPersonAvailableSlots] =
        React.useState<IAvailabilityWindow[]>();
    const [selectedClientToHoldId] = React.useState(
        parsedQueryParams["client"] || ""
    );
    const [showLoader, setShowLoader] = React.useState(false);
    const [clientRequestedCall, setClientRequestedCall] = React.useState(false);
    const [requestedService, setRequestedService] = React.useState(
        selectedClientToHold?.requested_service || ""
    );
    const [sessionAttendees, setSessionAttendees] = React.useState<
        ISessionAttendee[]
    >([]);
    const [practiceId, setPracticeId] = React.useState("");
    const [serviceDeliveryOption, setServiceDeliveryOption] =
        React.useState<ServiceDeliveryOption>();
    const [selectedPractice, setSelectedPractice] = React.useState("");

    const [intakeAvailabilityBySupervisor, setIntakeAvailabilityBySupervisor] =
        React.useState<{ [key: string]: IIntakeAvailability[] }>({});
    const [supervisorsWithIntake, setSupervisorsWithIntake] = React.useState<
        (IClinician & {
            supervisorRole?: "primary" | "secondary" | "not_current_supervisor";
            supervisorPracticeLabel?: string;
        })[]
    >([]);
    const [selectedIntakeSlot, setSelectedIntakeSlot] = React.useState<
        (IIntakeAvailability & { supervisorId: string }) | null
    >(null);
    const [showAllIntakeSlots, setShowAllIntakeSlots] = React.useState(false);
    const [isSupervisorIntakeNotRequired, setIsSupervisorIntakeNotRequired] =
        React.useState(false);
    const [supervisorIntakeNotRequiredReason, setSupervisorIntakeNotRequiredReason] =
        React.useState("");
    const defaultPracticeId = process.env.REACT_APP_MANTLE_PRACTICE_ID ?? "";

    let maxApptDatePossible = moment().add(MAX_APPT_DATE_DIFF_WEEKS, "weeks");
    const [clinicianAvailableSlots, setClinicianAvailableSlots] =
        React.useState<IAvailabilityWindow[]>();

    const getInPersonAvailableSlots = async () => {
        const slots = clinicianToBook.available.filter(
            (item) => item.location !== "Remote"
        );
        const inPersonOffices = clinicianToBook.office_windows.filter(
            (item) => item.location !== "Remote"
        );
        const slotsMatchingOfficeWindows = clinicianToBook.available.filter(
            (slot) => {
                const start = getNowEquivalent(
                    moment(getStringFromScheduleDate(slot.start))
                );
                const end = getNowEquivalent(
                    moment(getStringFromScheduleDate(slot.end))
                );
                return inPersonOffices.find((officeSlot) => {
                    const officeStart = moment(
                        getStringFromScheduleDate(officeSlot.start)
                    );
                    const officeEnd = moment(
                        getStringFromScheduleDate(officeSlot.end)
                    );
                    const startWindow = getNowEquivalent(officeStart);
                    const endWindow = getNowEquivalent(officeEnd);
                    return (
                        start.isBetween(startWindow, endWindow, "hours", "[]") &&
                        end.isBetween(startWindow, endWindow, "hours", "[]") &&
                        slot.location === "Remote"
                    );
                });
            }
        );
        return setInPersonAvailableSlots([...slots, ...slotsMatchingOfficeWindows]);
    };

    React.useEffect(() => {
        if (serviceDeliveryOption === "in_person" && inPersonAvailableSlots) {
            setClinicianAvailableSlots(
                getClinicianActualAvailableSlots(inPersonAvailableSlots)
            );
        } else if (serviceDeliveryOption === "remote") {
            setClinicianAvailableSlots(
                getClinicianActualAvailableSlots(clinicianToBook.available)
            );
        }
    }, [serviceDeliveryOption, selectedAppointmentFromCard]);

    React.useEffect(() => {
        let selectedAppointmentToHoldDate = new Date();
        let _availableApptStartingDates: moment.Moment[] = [];

        if (selectedApptToHoldId && (clinicianToBook.available || []).length) {
            ({ selectedAppointmentToHoldDate, _availableApptStartingDates } =
                getAvailableStartDates(
                    clinicianToBook,
                    selectedApptToHoldId,
                    selectedAppointmentToHoldDate,
                    _availableApptStartingDates
                ));
            setAvailableApptStartingDates(_availableApptStartingDates);
        }
    }, [clinicianToBook, selectedApptToHoldId]);

    React.useEffect(() => {
        if ((clinicianToBook.available || []).length) {
            const fetchInPersonData = async () => {
                await getInPersonAvailableSlots();
            };
            fetchInPersonData();
        }
    }, [clinicianToBook]);

    React.useEffect(() => {
        const [firstAvailableDate] = availableApptStartingDates;
        if (firstAvailableDate) {
            setSelectedApptDate(firstAvailableDate);
        }
    }, [availableApptStartingDates]);

    const disableBooking = () => {
        return (
            !Boolean(selectedClientToHoldId) ||
            (clinicianToBook?.departments?.length > 1 && selectedPractice === "")
        );
    };

    // start blocks of codes for when appointment is selected from card
    const selectedCardAppointment = clinicianToBook?.available?.filter(
        (item) => item.id === selectedApptToHoldId
    );

    // available slot display text for appointments selected from card
    // e.g. "Fri 08:00am · Bi-Weekly · Midtown 45th St, Room 3 · from 12/12"
    const formatSlotLabel = (slot?: {
        start?: any;
        location?: string;
        office?: string;
        recurrenceRule?: string;
    }) => {
        if (!slot) return "";
        const startMoment = moment(slot.start);
        const room = slot.office
            ? /^\d+$/.test(String(slot.office))
                ? `Room ${slot.office}`
                : slot.office
            : "";
        const place = [slot.location, room].filter(Boolean).join(", ");
        return [
            startMoment.format(DateFormats.DAY_TIME_FORMAT),
            slot.recurrenceRule,
            place,
            `from ${startMoment.format(DateFormats.DAY_MONTH_FORMAT)}`,
        ]
            .filter(Boolean)
            .join(" · ");
    };

    const selectedApptFromCardDisplayText = () => {
        if (!selectedCardAppointment || selectedCardAppointment.length === 0) {
            return "";
        }
        return formatSlotLabel(selectedCardAppointment[0]);
    };

    // update the option of selectedDeliveryService based on the location of selected appointment from card
    const selectedDeliveryOption = () => {
        if (
            selectedCardAppointment &&
            selectedCardAppointment[0]?.location === "Remote"
        ) {
            return "remote";
        } else {
            return "in_person";
        }
    };

    const selectedApptToHold = (clinicianToBook.available || []).find(
        (a) => a.id === selectedApptToHoldId
    );

    const slotDefaultService =
        selectedApptToHold && (selectedApptToHold as any)?.availableService
            ? ((selectedApptToHold as any).availableService as string)
            : "";

    React.useEffect(() => {
        setRequestedService(
            getBookingRequestedServiceDefault(
                selectedClientToHold?.requested_service,
                slotDefaultService
            )
        );
    }, [
        selectedApptToHoldId,
        selectedClientToHold?.requested_service,
        slotDefaultService,
    ]);

    const resolvedRequestedService = resolveBookingRequestedService(
        requestedService,
        selectedClientToHold?.requested_service,
        requestedServices
    );

    const supportsSessionAttendees = isSessionAttendeeSupportedService(
        resolvedRequestedService
    );
    const isMinorSession = isClientMinor(selectedClientToHold?.date_of_birth);
    const showsSessionAttendees =
        supportsSessionAttendees ||
        (isMinorSession &&
            !selectedClientToHold?.has_no_other_parent_guardian &&
            Boolean(selectedClientToHold?.other_parent?.trim()));

    React.useEffect(() => {
        if (!showsSessionAttendees) {
            setSessionAttendees([]);
            return;
        }

        setSessionAttendees((current) => {
            const withOtherParent = reconcileOtherParentConsentAttendee(
                selectedClientToHold,
                current,
                supportsSessionAttendees
            );
            const hasGeneralAttendee = withOtherParent.some(
                (attendee) =>
                    attendee.active !== false && attendee.guardian_role !== "other"
            );
            if (
                resolvedRequestedService === "Couples Therapy" &&
                !hasGeneralAttendee
            ) {
                return [
                    ...withOtherParent,
                    buildEmptySessionAttendee({ relationship: "Partner" }),
                ];
            }
            return withOtherParent;
        });
    }, [resolvedRequestedService, showsSessionAttendees, selectedClientToHold]);

    const isEligibleIntakeSessions = React.useMemo(() => {
        if (!requestedService) {
            return false;
        }
        const normalizedRequestedService =
            requestedServices[requestedService] || requestedService;
        const bookingForEligibility = {
            requested_service: normalizedRequestedService,
        } as IClientBooking;
        return isEligibleForIntakeSession(
            selectedClientToHold,
            clinicianToBook,
            bookingForEligibility
        );
    }, [selectedClientToHold, clinicianToBook, requestedService, requestedServices]);

    const effectiveBookingPracticeId = React.useMemo(() => {
        return (
            practiceId ||
            allDepartmentsAndServices[clinicianToBook?.departments?.[0]]
                ?.practice_id ||
            defaultPracticeId ||
            ""
        );
    }, [practiceId, clinicianToBook, allDepartmentsAndServices, defaultPracticeId]);

    const supervisorPracticeIdsById = React.useMemo(() => {
        const map = new Map<string, string[]>();
        supervisorsWithIntake.forEach((sup) => {
            const supPracticeIds = Array.from(
                new Set(
                    (sup.departments || [])
                        .map(
                            (department) =>
                                allDepartmentsAndServices[department]?.practice_id ||
                                ""
                        )
                        .filter(Boolean)
                )
            );
            if (sup.email) {
                map.set(sup.email, supPracticeIds);
            }
            if (sup.id) {
                map.set(sup.id, supPracticeIds);
            }
        });
        return map;
    }, [supervisorsWithIntake, allDepartmentsAndServices]);

    const isDirectSupervisorId = React.useCallback(
        (supervisorId: string) => {
            const directSupervisorIds = new Set<string>();
            if (clinicianToBook?.supervisor_id) {
                directSupervisorIds.add(clinicianToBook.supervisor_id);
            }
            (clinicianToBook?.secondary_supervisor_ids || []).forEach((pair) => {
                if (pair?.supervisor_id) {
                    directSupervisorIds.add(pair.supervisor_id);
                }
            });
            if (!directSupervisorIds.size) return false;
            if (directSupervisorIds.has(supervisorId)) return true;
            const sup = supervisorsWithIntake.find(
                (s) => s.email === supervisorId || s.id === supervisorId
            );
            if (!sup) return false;
            return (
                Boolean(sup.id && directSupervisorIds.has(sup.id)) ||
                Boolean(sup.email && directSupervisorIds.has(sup.email))
            );
        },
        [clinicianToBook, supervisorsWithIntake]
    );

    const getIntakeSlotPracticeLabel = (
        slot: IIntakeAvailability & { supervisorId: string }
    ) => {
        if (!slot.practice_id) {
            return "Practice not set";
        }
        return (
            findLabelByPracticeId(slot.practice_id, allDepartmentsAndServices) ||
            "Unknown practice"
        );
    };

    const allIntakeSlots = React.useMemo(() => {
        const slots: (IIntakeAvailability & { supervisorId: string })[] = [];
        Object.entries(intakeAvailabilityBySupervisor).forEach(
            ([supervisorId, supervisorSlots]) => {
                supervisorSlots.forEach((slot) => {
                    if (
                        effectiveBookingPracticeId &&
                        slot.practice_id !== effectiveBookingPracticeId &&
                        !(
                            !slot.practice_id &&
                            ((defaultPracticeId &&
                                effectiveBookingPracticeId === defaultPracticeId) ||
                                (
                                    supervisorPracticeIdsById.get(supervisorId) || []
                                ).includes(effectiveBookingPracticeId))
                        )
                    ) {
                        return;
                    }
                    slots.push({
                        ...slot,
                        supervisorId,
                    });
                });
            }
        );
        return slots.sort((a, b) => {
            const aIsDirect = isDirectSupervisorId(a.supervisorId);
            const bIsDirect = isDirectSupervisorId(b.supervisorId);
            if (aIsDirect !== bIsDirect) {
                return aIsDirect ? -1 : 1;
            }
            return moment(a.start_time).diff(moment(b.start_time));
        });
    }, [
        intakeAvailabilityBySupervisor,
        effectiveBookingPracticeId,
        defaultPracticeId,
        supervisorPracticeIdsById,
        isDirectSupervisorId,
    ]);

    const formattedIntakeSlots = React.useMemo(() => {
        return formatIntakeAvailabilitySlots(allIntakeSlots);
    }, [allIntakeSlots]);
    const visibleIntakeSlots = React.useMemo(
        () => (showAllIntakeSlots ? allIntakeSlots : allIntakeSlots.slice(0, 10)),
        [allIntakeSlots, showAllIntakeSlots]
    );
    const hasAvailableIntakeSlots = allIntakeSlots.length > 0;

    React.useEffect(() => {
        const loadIntakeAvailability = async () => {
            try {
                const { availability, enrichedSupervisors } =
                    await loadSupervisorsWithIntake(
                        clinicianToBook,
                        allDepartmentsAndServices,
                        store.fetchAllSupervisors
                    );
                setIntakeAvailabilityBySupervisor(availability);
                setSupervisorsWithIntake(enrichedSupervisors);
            } catch (error) {
                console.error("Failed to load intake availability", error);
                setIntakeAvailabilityBySupervisor({});
                setSupervisorsWithIntake([]);
            }
        };

        if (isEligibleIntakeSessions) {
            loadIntakeAvailability();
            return;
        }

        setIntakeAvailabilityBySupervisor({});
        setSupervisorsWithIntake([]);
        setSelectedIntakeSlot(null);
        setShowAllIntakeSlots(false);
        setIsSupervisorIntakeNotRequired(false);
        setSupervisorIntakeNotRequiredReason("");
    }, [isEligibleIntakeSessions, clinicianToBook, allDepartmentsAndServices]);

    const intakeSkipReasonTrimmed = supervisorIntakeNotRequiredReason.trim();

    const activeSessionAttendees = sessionAttendees.filter(
        (attendee) => attendee.active !== false
    );

    const handleAddSessionAttendee = () => {
        setSessionAttendees((prev) => [...prev, buildEmptySessionAttendee()]);
    };

    const handleSessionAttendeeChange = (
        attendeeId: string,
        changes: Partial<ISessionAttendee>
    ) => {
        setSessionAttendees((prev) =>
            prev.map((attendee) => {
                if (attendee.id !== attendeeId) return attendee;
                const updatedAttendee = {
                    ...attendee,
                    ...changes,
                    updated_at: new Date(),
                };
                return {
                    ...updatedAttendee,
                    consent_check: getUpdatedAttendeeConsentCheck(updatedAttendee),
                };
            })
        );
    };

    const handleRemoveSessionAttendee = (attendeeId: string) => {
        setSessionAttendees((prev) =>
            prev.map((attendee) =>
                attendee.id === attendeeId
                    ? { ...attendee, active: false, updated_at: new Date() }
                    : attendee
            )
        );
    };

    const holdBooking = async () => {
        if (!selectedApptToHoldId || !selectedClientToHoldId) {
            return;
        }
        const heldValidationMessage =
            getAppointmentHeldValidationMessage(selectedClientToHold);
        if (heldValidationMessage) {
            addToast(heldValidationMessage, {
                appearance: "error",
                autoDismiss: false,
            });
            return;
        }
        if (
            isEligibleIntakeSessions &&
            hasAvailableIntakeSlots &&
            !selectedIntakeSlot &&
            (!isSupervisorIntakeNotRequired || !intakeSkipReasonTrimmed)
        ) {
            addToast(
                "Select a supervisor intake session or enter a reason to proceed without one.",
                { appearance: "error" }
            );
            return;
        }

        setShowLoader(true);
        try {
            const emailSignature = getIntakeEmailSignature(olc);
            const result = await store.holdAppointment({
                clientId: selectedClientToHoldId as string,
                clinicianId: clinicianToBook.id || clinicianToBook.email,
                availableWindowId: selectedApptToHoldId,
                startingDate: selectedApptDate.toDate(),
                clientRequestedCall,
                requestedService: resolvedRequestedService,
                clientConcerns: clientConcerns || "",
                clientCallNotes: clientCallNotes || "",
                serviceDeliveryOption:
                    serviceDeliveryOption || selectedDeliveryOption(),
                last_updated_by: loggedInUserEmail!,
                practice_id: effectiveBookingPracticeId,
                department:
                    findLabelByPracticeId(
                        effectiveBookingPracticeId,
                        allDepartmentsAndServices
                    ) || "",
                sessionAttendees: showsSessionAttendees
                    ? sessionAttendees.map((attendee) => ({
                          ...attendee,
                          first_name: attendee.first_name.trim(),
                          last_name: attendee.last_name.trim(),
                          email: attendee.email?.trim() || "",
                          consent_check: getUpdatedAttendeeConsentCheck(attendee),
                      }))
                    : undefined,
                supervisorIntakePayload:
                    isEligibleIntakeSessions && selectedIntakeSlot
                        ? {
                              selectedSlot: selectedIntakeSlot,
                              supervisorsWithIntake,
                          }
                        : undefined,
                appointmentChecksUpdate:
                    isEligibleIntakeSessions && isSupervisorIntakeNotRequired
                        ? {
                              supervisor_intake_not_required: true,
                              supervisor_intake_not_required_reason:
                                  intakeSkipReasonTrimmed,
                          }
                        : undefined,
                signature: emailSignature,
            });
            if (!result) {
                addToast("Failed to hold appointment", { appearance: "error" });
                return;
            }

            if (isEligibleIntakeSessions && selectedIntakeSlot) {
                try {
                    await store.updateIntakeSessionBooking(
                        selectedIntakeSlot.supervisorId,
                        selectedIntakeSlot.id,
                        selectedApptToHoldId,
                        selectedClientToHoldId as string,
                        `${selectedClientToHold.first_name} ${selectedClientToHold.last_name[0]}.`,
                        "held",
                        selectedIntakeSlot.practice_id || effectiveBookingPracticeId,
                        clinicianToBook.fullname
                    );
                } catch (error) {
                    await store.updateBooking(
                        clinicianToBook.id || clinicianToBook.email,
                        selectedClientToHoldId as string,
                        selectedApptToHoldId,
                        { supervisor_intake_session: null },
                        {},
                        "held",
                        loggedInUserEmail!
                    );
                    addToast(
                        "Failed to reserve selected intake session. Booking was held without supervisor intake session.",
                        { appearance: "error" }
                    );
                    return;
                }
            }

            addToast("Client appointment held.", {
                appearance: "success",
            });
            push({
                pathname: `/v2/client-full-view`,
                search: `?clientId=${selectedClientToHoldId}&activeTab=${ClientStatus.APPT_HELD}`,
                state: { client: selectedClientToHold, enableOtherStatuses: true },
            });
        } finally {
            setShowLoader(false);
        }
    };

    // Retrieve the services for the requested department
    const departmentServices =
        clinicianServiceSelections[clinicianToBook?.id as string]?.services;

    // Generate options for selects
    const deliveryOptions = [
        { value: "in_person", label: "In-Person" },
        { value: "remote", label: "Telemed" },
    ];

    const availableSlotOptions = selectedAppointmentFromCard
        ? [
              {
                  value: selectedCardAppointment
                      ? selectedCardAppointment[0]?.id
                      : "",
                  label: selectedApptFromCardDisplayText(),
              },
          ]
        : clinicianAvailableSlots?.map((a) => ({
              value: a.id,
              label: formatSlotLabel(a),
          })) || [];

    const selectedSlotLabel =
        availableSlotOptions.find((option) => option.value === selectedApptToHoldId)
            ?.label || "";
    const clientDisplayName = [
        selectedClientToHold?.first_name,
        selectedClientToHold?.last_name,
    ]
        .filter(Boolean)
        .join(" ");

    const practiceOptions =
        clinicianToBook?.departments?.map((department) => ({
            value: department,
            label: department?.replaceAll("_", " "),
        })) || [];

    const serviceOptions =
        departmentServices?.map((s, idx) => ({
            value: s,
            label: toTitleCase(s?.replaceAll("_", " ")),
        })) || [];

    return (
        <>
            <Dialog
                open={Boolean(clinicianToBook.id)}
                onClose={() => {
                    setClinicianToBook({} as IClinician);
                    setIsSupervisorIntakeNotRequired(false);
                    setSupervisorIntakeNotRequiredReason("");
                }}
                title={`Hold client for ${clinicianToBook.fullname}`}
                onSave={holdBooking}
                onCancel={() => {
                    setClinicianToBook({} as IClinician);
                    setIsSupervisorIntakeNotRequired(false);
                    setSupervisorIntakeNotRequiredReason("");
                }}
                saveText="Hold Booking"
                cancelText="Cancel"
                isSubmitBtnDisabled={
                    disableBooking() ||
                    (isEligibleIntakeSessions &&
                        hasAvailableIntakeSlots &&
                        !selectedIntakeSlot &&
                        (!isSupervisorIntakeNotRequired || !intakeSkipReasonTrimmed))
                }
                wrapperClassName={styles.bookingDialogWrapper}
            >
                <div className={styles.holdLayout}>
                    <div className={cn("v2-form", styles.holdMain)}>
                        {clinicianToBook.available && (
                            <>
                                <section className={styles.formSection}>
                                    <h3 className={styles.formSectionTitle}>
                                        Appointment
                                    </h3>
                                    <Select
                                        label="Available slot"
                                        options={availableSlotOptions}
                                        value={
                                            availableSlotOptions.find(
                                                (option) =>
                                                    option.value ===
                                                    selectedApptToHoldId
                                            ) || { label: "", value: "" }
                                        }
                                        isRequired
                                        onChange={(val) => {
                                            setSelectedAppointmentFromCard(false);
                                            setSelectedApptToHoldId(
                                                (val as Option)?.value
                                            );
                                        }}
                                        isLongListInDialog
                                    />
                                    <div className={styles.fieldRow}>
                                        <Select
                                            label="In-Person or Telemed"
                                            options={deliveryOptions}
                                            value={
                                                deliveryOptions.find(
                                                    (option) =>
                                                        option.value ===
                                                        (selectedAppointmentFromCard
                                                            ? selectedDeliveryOption()
                                                            : serviceDeliveryOption)
                                                ) || { label: "", value: "" }
                                            }
                                            isRequired
                                            onChange={(val) => {
                                                setSelectedAppointmentFromCard(
                                                    false
                                                );
                                                setServiceDeliveryOption(
                                                    (val as any)
                                                        ?.value as ServiceDeliveryOption
                                                );
                                            }}
                                            isLongListInDialog
                                        />
                                        <Select
                                            label="Requested Service"
                                            options={serviceOptions}
                                            value={
                                                serviceOptions.find(
                                                    (option) =>
                                                        option.value ===
                                                        requestedService
                                                ) || { label: "", value: "" }
                                            }
                                            isRequired
                                            onChange={(val) => {
                                                setRequestedService(
                                                    (val as any)?.value
                                                );
                                            }}
                                            isLongListInDialog
                                        />
                                    </div>
                                    <div className={styles.fieldRow}>
                                        <div>
                                            <DatePicker
                                                id={`pick-${selectedApptToHoldId}`}
                                                dateFormat={"MM/dd/yyyy"}
                                                label="Starting on"
                                                minDate={
                                                    [
                                                        ClinicianCredential.MHCLP,
                                                        ClinicianCredential.LMSW,
                                                    ].includes(
                                                        clinicianToBook.credential
                                                    )
                                                        ? moment()
                                                              .add(1, "week")
                                                              .toDate()
                                                        : moment().toDate()
                                                }
                                                includeDates={availableApptStartingDates.map(
                                                    (a) => a.toDate()
                                                )}
                                                maxDate={maxApptDatePossible.toDate()}
                                                selected={selectedApptDate.toDate()}
                                                onChange={(date) => {
                                                    setSelectedApptDate(
                                                        moment(date as Date)
                                                    );
                                                }}
                                            />
                                            <div className={styles.startDates}>
                                                {availableApptStartingDates.length >
                                                0 ? (
                                                    <>
                                                        <span
                                                            className={
                                                                styles.startDatesLabel
                                                            }
                                                        >
                                                            Available start dates
                                                        </span>
                                                        {availableApptStartingDates.map(
                                                            (a) => (
                                                                <span
                                                                    key={a.toISOString()}
                                                                    className={cn(
                                                                        styles.startDateChip,
                                                                        a.isSame(
                                                                            selectedApptDate,
                                                                            "day"
                                                                        ) &&
                                                                            styles.startDateChipActive
                                                                    )}
                                                                >
                                                                    {a.format(
                                                                        DateFormats.DAY_MONTH_FORMAT
                                                                    )}
                                                                </span>
                                                            )
                                                        )}
                                                    </>
                                                ) : (
                                                    <span
                                                        className={
                                                            styles.startDatesLabel
                                                        }
                                                    >
                                                        No start dates in the next 4
                                                        weeks
                                                    </span>
                                                )}
                                                {selectedApptToHold?.ending_date && (
                                                    <span
                                                        className={
                                                            styles.startDatesLabel
                                                        }
                                                    >
                                                        · Ends{" "}
                                                        {moment(
                                                            selectedApptToHold?.ending_date
                                                        ).format(
                                                            DateFormats.DAY_MONTH_FORMAT
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        {clinicianToBook?.departments?.length >
                                            1 && (
                                            <Select
                                                label="Clinician Practice"
                                                options={practiceOptions}
                                                value={
                                                    practiceOptions.find(
                                                        (option) =>
                                                            option.value ===
                                                            selectedPractice
                                                    ) || { label: "", value: "" }
                                                }
                                                isRequired
                                                onChange={(val) => {
                                                    setSelectedPractice(
                                                        (val as any)?.value
                                                    );
                                                    setPracticeId(
                                                        allDepartmentsAndServices[
                                                            (val as any)?.value
                                                        ]?.practice_id as string
                                                    );
                                                }}
                                                isLongListInDialog
                                            />
                                        )}
                                    </div>
                                </section>
                                {showsSessionAttendees && (
                                    <div className={styles.sessionAttendeesSection}>
                                        <div
                                            className={styles.sessionAttendeesHeader}
                                        >
                                            <h3 className={styles.formSectionTitle}>
                                                {supportsSessionAttendees
                                                    ? "Session attendees"
                                                    : "Other parent/guardian"}
                                            </h3>
                                            {supportsSessionAttendees && (
                                                <Button
                                                    variant="secondary"
                                                    onClick={
                                                        handleAddSessionAttendee
                                                    }
                                                >
                                                    + Add attendee
                                                </Button>
                                            )}
                                        </div>
                                        {activeSessionAttendees.length === 0 && (
                                            <p className={styles.infoText}>
                                                {supportsSessionAttendees
                                                    ? "Add non-client attendees for this session."
                                                    : "No other parent/guardian is on file."}
                                            </p>
                                        )}
                                        {activeSessionAttendees.map(
                                            (attendee, index) => {
                                                const missingFields =
                                                    getSessionAttendeeMissingFields(
                                                        attendee,
                                                        resolvedRequestedService
                                                    );
                                                const isMinor = isMinorDob(
                                                    attendee.date_of_birth
                                                );
                                                const dobDate =
                                                    attendee.date_of_birth
                                                        ? moment(
                                                              attendee.date_of_birth
                                                          ).toDate()
                                                        : null;
                                                const attendeeNumber =
                                                    activeSessionAttendees
                                                        .slice(0, index)
                                                        .filter(
                                                            (entry) =>
                                                                entry.guardian_role !==
                                                                "other"
                                                        ).length + 1;

                                                return (
                                                    <div
                                                        key={attendee.id}
                                                        className={
                                                            styles.attendeeBlock
                                                        }
                                                    >
                                                        <div
                                                            className={
                                                                styles.sessionAttendeesHeader
                                                            }
                                                        >
                                                            <p
                                                                className={
                                                                    styles.intakeAvailabilityHeader
                                                                }
                                                            >
                                                                {attendee.guardian_role ===
                                                                "other"
                                                                    ? "Other Parent/Guardian"
                                                                    : `Attendee ${attendeeNumber}${
                                                                          isMinor
                                                                              ? " (Minor)"
                                                                              : ""
                                                                      }`}
                                                            </p>
                                                            {attendee.guardian_role !==
                                                                "other" && (
                                                                <Button
                                                                    variant="normal"
                                                                    size="small"
                                                                    className={
                                                                        styles.removeAttendee
                                                                    }
                                                                    onClick={() =>
                                                                        handleRemoveSessionAttendee(
                                                                            attendee.id
                                                                        )
                                                                    }
                                                                >
                                                                    Remove
                                                                </Button>
                                                            )}
                                                        </div>
                                                        <div
                                                            className={[
                                                                styles.attendeeFieldsGrid,
                                                                resolvedRequestedService ===
                                                                "Family Therapy"
                                                                    ? styles.attendeeFieldsGridFive
                                                                    : "",
                                                            ]
                                                                .filter(Boolean)
                                                                .join(" ")}
                                                        >
                                                            <Input
                                                                label="First name"
                                                                isRequired
                                                                value={
                                                                    attendee.first_name
                                                                }
                                                                hasError={
                                                                    missingFields.includes(
                                                                        "name"
                                                                    ) &&
                                                                    !attendee.first_name.trim()
                                                                }
                                                                errorText="Required"
                                                                onChange={(event) =>
                                                                    handleSessionAttendeeChange(
                                                                        attendee.id,
                                                                        {
                                                                            first_name:
                                                                                event
                                                                                    .target
                                                                                    .value,
                                                                        }
                                                                    )
                                                                }
                                                                inputSize="small"
                                                            />
                                                            <Input
                                                                label="Last name"
                                                                isRequired
                                                                value={
                                                                    attendee.last_name
                                                                }
                                                                hasError={
                                                                    missingFields.includes(
                                                                        "name"
                                                                    ) &&
                                                                    !attendee.last_name.trim()
                                                                }
                                                                errorText="Required"
                                                                onChange={(event) =>
                                                                    handleSessionAttendeeChange(
                                                                        attendee.id,
                                                                        {
                                                                            last_name:
                                                                                event
                                                                                    .target
                                                                                    .value,
                                                                        }
                                                                    )
                                                                }
                                                                inputSize="small"
                                                            />
                                                            <Input
                                                                label="Relationship"
                                                                value={
                                                                    attendee.relationship ||
                                                                    ""
                                                                }
                                                                onChange={(event) =>
                                                                    handleSessionAttendeeChange(
                                                                        attendee.id,
                                                                        {
                                                                            relationship:
                                                                                event
                                                                                    .target
                                                                                    .value,
                                                                        }
                                                                    )
                                                                }
                                                                inputSize="small"
                                                            />
                                                            <Input
                                                                type="email"
                                                                label="Email"
                                                                isRequired
                                                                value={
                                                                    attendee.email ||
                                                                    ""
                                                                }
                                                                hasError={missingFields.includes(
                                                                    "email"
                                                                )}
                                                                onChange={(event) =>
                                                                    handleSessionAttendeeChange(
                                                                        attendee.id,
                                                                        {
                                                                            email: event
                                                                                .target
                                                                                .value,
                                                                        }
                                                                    )
                                                                }
                                                                inputSize="small"
                                                            />
                                                            {resolvedRequestedService ===
                                                                "Family Therapy" &&
                                                            attendee.guardian_role !==
                                                                "other" ? (
                                                                <DatePicker
                                                                    label="Date of birth"
                                                                    isRequired
                                                                    dateFormat="MM/dd/yyyy"
                                                                    selected={
                                                                        dobDate
                                                                    }
                                                                    hasError={missingFields.includes(
                                                                        "date_of_birth"
                                                                    )}
                                                                    errorText="Required"
                                                                    placeholderText="Date of birth"
                                                                    onChange={(
                                                                        date
                                                                    ) =>
                                                                        handleSessionAttendeeChange(
                                                                            attendee.id,
                                                                            {
                                                                                date_of_birth:
                                                                                    date
                                                                                        ? moment(
                                                                                              date as Date
                                                                                          ).format(
                                                                                              "YYYY-MM-DD"
                                                                                          )
                                                                                        : null,
                                                                            }
                                                                        )
                                                                    }
                                                                    inputSize="small"
                                                                    popperPlacement="bottom-start"
                                                                />
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        )}
                                    </div>
                                )}
                                {isEligibleIntakeSessions && (
                                    <div
                                        className={styles.intakeAvailabilitySection}
                                    >
                                        <h3 className={styles.formSectionTitle}>
                                            Supervisor intake
                                        </h3>
                                        {requestedService && (
                                            <Alert
                                                type="warning"
                                                icon={<InfoIcon />}
                                            >
                                                This appointment requires an Intake
                                                session
                                                {hasAvailableIntakeSlots
                                                    ? ""
                                                    : " but there are no intake slots available. You can proceed but you will need to set one before the appointment can be confirmed."}
                                            </Alert>
                                        )}

                                        {!isSupervisorIntakeNotRequired && (
                                            <div
                                                className={
                                                    styles.intakeAvailabilityWrapper
                                                }
                                            >
                                                {Object.keys(formattedIntakeSlots)
                                                    .length === 0 ? (
                                                    <p className={styles.noSlots}>
                                                        No available intake session
                                                        slots
                                                    </p>
                                                ) : (
                                                    <div
                                                        className={
                                                            styles.intakeAvailabilityWrapper
                                                        }
                                                    >
                                                        <p
                                                            className={
                                                                styles.intakeAvailabilityHeader
                                                            }
                                                        >
                                                            Available Intake Sessions
                                                        </p>
                                                        <div
                                                            className={
                                                                styles.intakeAvailabilitySlotsList
                                                            }
                                                        >
                                                            {visibleIntakeSlots.map(
                                                                (slot) => {
                                                                    const startTime =
                                                                        moment(
                                                                            slot.start_time
                                                                        );
                                                                    const slotLabel = `${startTime.format(
                                                                        "ddd, MMM Do"
                                                                    )} - ${startTime.format(
                                                                        DateFormats.HOUR_MIN_FORMAT
                                                                    )}`;
                                                                    const isSelected =
                                                                        selectedIntakeSlot?.id ===
                                                                            slot.id &&
                                                                        selectedIntakeSlot?.supervisorId ===
                                                                            slot.supervisorId;
                                                                    const isDirectSupervisor =
                                                                        isDirectSupervisorId(
                                                                            slot.supervisorId
                                                                        );
                                                                    const practiceLabel =
                                                                        getIntakeSlotPracticeLabel(
                                                                            slot
                                                                        );
                                                                    return (
                                                                        <button
                                                                            key={`${slot.supervisorId}-${slot.id}`}
                                                                            onClick={() => {
                                                                                if (
                                                                                    isSelected
                                                                                ) {
                                                                                    setSelectedIntakeSlot(
                                                                                        null
                                                                                    );
                                                                                } else {
                                                                                    setSelectedIntakeSlot(
                                                                                        slot
                                                                                    );
                                                                                    setIsSupervisorIntakeNotRequired(
                                                                                        false
                                                                                    );
                                                                                    setSupervisorIntakeNotRequiredReason(
                                                                                        ""
                                                                                    );
                                                                                }
                                                                            }}
                                                                            className={`${
                                                                                styles.timeSlot
                                                                            } ${
                                                                                isSelected
                                                                                    ? styles.selectedSlot
                                                                                    : ""
                                                                            } ${
                                                                                isDirectSupervisor
                                                                                    ? styles.directSupervisorSlot
                                                                                    : ""
                                                                            }`}
                                                                            aria-pressed={
                                                                                isSelected
                                                                            }
                                                                        >
                                                                            {
                                                                                slotLabel
                                                                            }

                                                                            <span
                                                                                className={
                                                                                    styles.supervisorName
                                                                                }
                                                                            >
                                                                                (
                                                                                {
                                                                                    slot.clinician_display_name
                                                                                }
                                                                                {
                                                                                    " - "
                                                                                }
                                                                                {
                                                                                    practiceLabel
                                                                                }
                                                                                )
                                                                            </span>
                                                                            {isDirectSupervisor && (
                                                                                <span
                                                                                    className={
                                                                                        styles.directTag
                                                                                    }
                                                                                >
                                                                                    Direct
                                                                                    supervisor
                                                                                </span>
                                                                            )}
                                                                        </button>
                                                                    );
                                                                }
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {!isSupervisorIntakeNotRequired &&
                                            allIntakeSlots.length > 10 && (
                                                <div
                                                    className={
                                                        styles.intakeSlotsToggleActions
                                                    }
                                                >
                                                    {!showAllIntakeSlots ? (
                                                        <button
                                                            type="button"
                                                            className={
                                                                styles.intakeSlotsToggleButton
                                                            }
                                                            onClick={() =>
                                                                setShowAllIntakeSlots(
                                                                    true
                                                                )
                                                            }
                                                        >
                                                            Show More
                                                        </button>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className={
                                                                styles.intakeSlotsToggleButton
                                                            }
                                                            onClick={() =>
                                                                setShowAllIntakeSlots(
                                                                    false
                                                                )
                                                            }
                                                        >
                                                            Show Less
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        <FormCheckbox
                                            wrapperClass={
                                                styles.intakeNotRequiredCheckboxWrapper
                                            }
                                            label="Supervisor intake not required for this booking"
                                            checked={isSupervisorIntakeNotRequired}
                                            onChange={() => {
                                                const nextValue =
                                                    !isSupervisorIntakeNotRequired;
                                                setIsSupervisorIntakeNotRequired(
                                                    nextValue
                                                );
                                                if (nextValue) {
                                                    setSelectedIntakeSlot(null);
                                                } else {
                                                    setSupervisorIntakeNotRequiredReason(
                                                        ""
                                                    );
                                                }
                                            }}
                                        />
                                        {isSupervisorIntakeNotRequired && (
                                            <div
                                                className={
                                                    styles.intakeSkipReasonWrap
                                                }
                                            >
                                                <TextArea
                                                    label="Reason for proceeding without a supervisor intake session"
                                                    value={
                                                        supervisorIntakeNotRequiredReason
                                                    }
                                                    onChange={(e) =>
                                                        setSupervisorIntakeNotRequiredReason(
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder="Required if you are not selecting an intake session"
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <aside className={styles.holdSide}>
                        <div className={styles.holdSummary}>
                            <p className={styles.holdSummaryTitle}>
                                {clientDisplayName ? (
                                    <>
                                        <strong>{clientDisplayName}</strong> with{" "}
                                    </>
                                ) : null}
                                <strong>{clinicianToBook.fullname}</strong>
                            </p>
                            {selectedSlotLabel && (
                                <p className={styles.holdSummaryDetail}>
                                    {selectedSlotLabel}
                                    {selectedApptDate &&
                                        ` · starting ${selectedApptDate.format(
                                            DateFormats.DAY_MONTH_FORMAT
                                        )}`}
                                </p>
                            )}
                        </div>
                        {clinicianToBook?.departments?.length > 0 && (
                            <Alert type="info" icon={<InfoIcon />}>
                                <div className={styles.departmentAlert}>
                                    <p>
                                        This clinician belongs to the following
                                        practice
                                    </p>
                                    <div className={styles.badgesWrapper}>
                                        {clinicianToBook?.departments?.map(
                                            (dep, idx) => (
                                                <Badge
                                                    key={idx}
                                                    content={dep?.replaceAll(
                                                        "_",
                                                        " "
                                                    )}
                                                    textColor={
                                                        getV2BadgePropsByPractice(
                                                            dep
                                                        ).textColor
                                                    }
                                                    bgColor={
                                                        getV2BadgePropsByPractice(
                                                            dep
                                                        ).bgColor
                                                    }
                                                />
                                            )
                                        )}
                                    </div>
                                </div>
                            </Alert>
                        )}

                        {clinicianToBook.available && (
                            <section className={styles.formSection}>
                                <h3 className={styles.formSectionTitle}>
                                    Consult call
                                </h3>
                                <div className={styles.consultSection}>
                                    <FormCheckbox
                                        wrapperClass={styles.consultCheckbox}
                                        label="Request a consult call"
                                        checked={clientRequestedCall}
                                        onChange={() => {
                                            setClientRequestedCall(
                                                !clientRequestedCall
                                            );
                                        }}
                                    />

                                    <div className={styles.clientConcerns}>
                                        <TextArea
                                            label="Client concerns (for consult call)"
                                            value={clientConcerns}
                                            onChange={(e) =>
                                                setClientConcerns(e.target.value)
                                            }
                                        />
                                        <p className={styles.infoText}>
                                            If the client has requested a consult
                                            call, this would be sent to the clinician
                                            along with the appointment held
                                            notification.
                                        </p>
                                    </div>
                                </div>
                            </section>
                        )}
                    </aside>
                </div>
            </Dialog>
            <Loader show={showLoader} />
        </>
    );
}
